import { useId, useState } from 'react';
import { activeTakeOf, type CourseData } from '@slideforge/shared';
import { getClip } from '../audio/blob-store.js';
import { applyCompileDrops, fidelityDebt, type FidelityReport } from '@slideforge/shared';
import { publish, type PublishTarget } from '@slideforge/publisher';
import { useStrings } from '../i18n/context.js';
import type { SlideForgeState } from '../state/useSlideForge.js';
import { formatBytes } from './DeckPanel.js';

const TARGETS: readonly PublishTarget[] = [
  'scorm12',
  'scorm2004',
  'xapi',
  'cmi5',
  'html5',
  'html5-single-file',
];

export interface PublishPanelProps {
  readonly state: SlideForgeState;
  /**
   * Fetches the built player runtime. Injected so the panel can be tested and
   * so Local mode can serve it from the add-in's own origin — the constitution
   * allows no outbound calls except the Office.js CDN.
   */
  readonly loadPlayerScript?: () => Promise<string>;
}

type Status = { kind: 'idle' } | { kind: 'busy'; stage: string } | { kind: 'done'; bytes: number; url: string; name: string } | { kind: 'error'; message: string };

export function PublishPanel({ state, loadPlayerScript = fetchPlayerScript }: PublishPanelProps) {
  const t = useStrings();
  const base = useId();
  const [target, setTarget] = useState<PublishTarget>('scorm12');
  const [status, setStatus] = useState<Status>({ kind: 'idle' });

  const parsed = state.deck.parsed;
  const report = parsed && state.deck.fidelity ? state.deck.fidelity : undefined;
  const blocked = (report?.blocking.length ?? 0) > 0;

  const run = async () => {
    if (!parsed || !report) return;
    setStatus({ kind: 'busy', stage: t.publishing });
    try {
      const playerScript = await loadPlayerScript();
      // Narration audio lives in the local audio store, not in the deck, so the
      // bytes have to be gathered before the package is written.
      const narrationBytes = await collectNarrationBytes(state.course);
      const result = await publish({
        deck: parsed.deck,
        course: state.course,
        diagnostics: parsed.diagnostics,
        fidelity: report,
        target,
        mediaBytes: parsed.media.bytes,
        narrationBytes,
        playerScript,
        onProgress: (e) => setStatus({ kind: 'busy', stage: e.stage }),
      });
      const blob = new Blob([result.zip.slice() as unknown as BlobPart], { type: 'application/zip' });
      setStatus({
        kind: 'done',
        bytes: result.zip.byteLength,
        url: URL.createObjectURL(blob),
        name: `${slugify(state.course.title || parsed.deck.title)}-${target}.zip`,
      });
    } catch (error) {
      setStatus({ kind: 'error', message: error instanceof Error ? error.message : String(error) });
    }
  };

  return (
    <section aria-labelledby="sf-publish-heading" className="sf-panel">
      <h2 id="sf-publish-heading">{t.publishHeading}</h2>

      <div className="sf-field">
        <label htmlFor={`${base}-target`}>{t.target}</label>
        <select
          id={`${base}-target`}
          value={target}
          onChange={(e) => setTarget(e.target.value as PublishTarget)}
        >
          {TARGETS.map((value) => (
            <option key={value} value={value}>
              {t.targetNames[value] ?? value}
            </option>
          ))}
        </select>
      </div>

      {report && <FidelitySummary report={report} />}

      {blocked && (
        <p role="alert" className="sf-error">
          {t.publishBlocked}
        </p>
      )}

      <button
        type="button"
        className="sf-primary"
        onClick={() => void run()}
        disabled={!parsed || blocked || status.kind === 'busy'}
      >
        {status.kind === 'busy' ? t.publishing : t.publishButton}
      </button>

      <p role="status" className="sf-status">
        {status.kind === 'busy' && status.stage}
        {status.kind === 'error' && status.message}
      </p>

      {status.kind === 'done' && (
        <p>
          <a href={status.url} download={status.name} className="sf-primary sf-download">
            {t.downloadPackage}
          </a>{' '}
          <span className="sf-muted">{`${t.estimatedSize}: ${formatBytes(status.bytes)}`}</span>
        </p>
      )}
    </section>
  );
}

/**
 * The report the author sees BEFORE publishing (§5.1 E, §5.4). Showing it
 * afterwards would be an apology rather than a decision.
 */
function FidelitySummary({ report }: { readonly report: FidelityReport }) {
  const t = useStrings();
  const debt = fidelityDebt(report);
  const imperfect = report.slides.filter((s) => s.strategy !== 'Vector');

  return (
    <section aria-labelledby="sf-fidelity-heading" className="sf-fidelity">
      <h3 id="sf-fidelity-heading">{t.fidelityHeading}</h3>

      {report.blocking.length > 0 && (
        <>
          <h4>{t.blockingHeading}</h4>
          <ul>
            {report.blocking.map((d, i) => (
              <li key={`${d.code}-${i}`} className="sf-severity-blocking">
                {d.message}
              </li>
            ))}
          </ul>
        </>
      )}

      {imperfect.length === 0 && debt.length === 0 ? (
        <p>{t.fidelityClean}</p>
      ) : (
        <>
          <ul className="sf-fidelity-slides">
            {imperfect.map((slide) => (
              <li key={slide.slideIndex}>
                <strong>{t.slideNumber(slide.slideIndex + 1)}</strong>
                {` — ${slide.strategy}`}
                <ul>
                  {slide.reasons.map((reason, i) => (
                    <li key={i}>{reason}</li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>

          {debt.length > 0 && (
            <>
              <h4>{t.droppedHeading}</h4>
              <ul>
                {debt.map((entry) => (
                  <li key={entry.construct}>
                    <code>{entry.construct}</code>
                    {` — ${entry.slides.map((n) => n + 1).join(', ')}`}
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </section>
  );
}

async function fetchPlayerScript(): Promise<string> {
  // Same origin as the add-in, so Local mode makes no outbound request.
  const response = await fetch('player.js');
  if (!response.ok) {
    throw new Error('The course player could not be loaded from the add-in. Reinstall SlideForge.');
  }
  return response.text();
}

function slugify(value: string): string {
  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'course'
  );
}

export { applyCompileDrops };

/**
 * Pulls every active narration take out of the local audio store.
 *
 * Only active takes are fetched: alternates are authoring history, and reading
 * them would multiply both the work and the package size for no benefit.
 */
async function collectNarrationBytes(course: CourseData): Promise<Map<string, Uint8Array>> {
  const bytes = new Map<string, Uint8Array>();
  for (const namespaces of Object.values(course.slides)) {
    const take = activeTakeOf(namespaces.narration);
    if (!take || bytes.has(take.audioId)) continue;
    const clip = await getClip(take.audioId);
    if (clip) bytes.set(take.audioId, new Uint8Array(await clip.blob.arrayBuffer()));
  }
  return bytes;
}
